import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMixer } from '../context/MixerContext';
import { profile, skills, businessSkills } from '../data/resumeData';
import { getSummary, getCurrentLens } from '../utils/contentTransform';
import './ProfileHeader.css';

const nameLetters = profile.name.split('');

export default function ProfileHeader() {
  const { mixerState } = useMixer();
  const summary = getSummary(profile, mixerState);
  const currentLens = getCurrentLens(mixerState);
  
  return (
    <motion.header 
      className="profile-header"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.1 }}
    >
      <div className="profile-header__main">
        <h1 className="profile-header__name">
          {nameLetters.map((letter, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.4, 
                delay: 0.1 + i * 0.03,
                ease: [0.22, 1, 0.36, 1]
              }}
              style={{ display: letter === ' ' ? 'inline' : 'inline-block' }}
            >
              {letter === ' ' ? '\u00A0' : letter}
            </motion.span>
          ))}
        </h1>
        
        <motion.div 
          className="profile-header__tagline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <span>Engineer</span>
          <span className="profile-header__tagline-divider">×</span>
          <span>Strategist</span>
        </motion.div>
        
        <div className="profile-header__lens">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentLens.join('-')}
              className="profile-header__lens-tags"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {currentLens.map((lens, i) => (
                <span key={lens} className="profile-header__lens-tag">
                  {lens}
                </span>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
        
        <AnimatePresence mode="wait">
          <motion.p 
            key={summary}
            className="profile-header__summary"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            {summary}
          </motion.p>
        </AnimatePresence>
      </div>
      
      <div className="profile-header__meta">
        <div className="profile-header__education">
          <span className="profile-header__meta-label">Education</span>
          {profile.education.map((edu, index) => (
            <div key={index} className={`profile-header__education-item ${edu.current ? 'profile-header__education-item--current' : ''}`}>
              <span className="profile-header__meta-value">{edu.degree}</span>
              <span className="profile-header__meta-sub">
                {edu.school}
                {edu.current && <span className="profile-header__current-badge">Current</span>}
              </span>
            </div>
          ))}
        </div>
        
        <div className="profile-header__contact">
          <span className="profile-header__meta-label">Contact</span>
          <a href={`mailto:${profile.contact.email}`} className="profile-header__link">
            {profile.contact.email}
          </a>
          <a href={`https://${profile.contact.linkedin}`} className="profile-header__link" target="_blank" rel="noopener noreferrer">
            LinkedIn
          </a>
          <a href={`https://${profile.contact.github}`} className="profile-header__link" target="_blank" rel="noopener noreferrer">
            GitHub
          </a>
        </div>
      </div>
      
      <div className="profile-header__skills-container">
        <div className="profile-header__skills">
          <span className="profile-header__meta-label">Core Technologies</span>
          <div className="profile-header__skill-list">
            {skills.slice(0, 6).map((skill, index) => (
              <motion.span 
                key={skill.name}
                className="profile-header__skill"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 + index * 0.05 }}
                whileHover={{ scale: 1.05 }}
              >
                {skill.name}
                <span className="profile-header__skill-level" style={{ width: `${skill.level}%` }} />
              </motion.span>
            ))}
          </div>
        </div>
        
        <div className="profile-header__skills profile-header__skills--business">
          <span className="profile-header__meta-label">Business & Strategy</span>
          <div className="profile-header__skill-list">
            {businessSkills.map((skill, index) => (
              <motion.span 
                key={skill.name}
                className={`profile-header__skill profile-header__skill--${skill.category}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.7 + index * 0.05 }}
                whileHover={{ scale: 1.05 }}
              >
                {skill.name}
              </motion.span>
            ))}
          </div>
        </div>
      </div>
    </motion.header>
  );
}
